// import { Search } from "@carbon/react";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  Search as SearchIcon,
  UserFilled as UserFilledIcon,
  Wallet as WalletIcon,
} from "@carbon/icons-react";

import { getPaymentStatusIndicator } from "../../utils";
import { Badge } from "../Badge";

import { Card } from "../Card";
import { CurrencyDisplay } from "../CurrencyDisplay";
import { SearchBar } from "../SearchBar";
import { SectionSubheader } from "../SectionSubheader";
import { IGeneralSearchBarViewProps } from "./GeneralSearchBar";
import { SwitchAppInlineLoadingIcon } from "../SwitchAppInlineLoadingIcon";
import { generalSearchConstants } from "../../constants";
import { useRef } from "react";
import { useClickOutside } from "../../hooks";

export const View = ({
  ref,
  isLoading,
  data,
  searchText,
  setSearchText,
  clearSearch,
  dropDownClassName,
  showDropDown,
  hideDropDown,
  isEmptyResult,
  isEmptyQuery,
}: IGeneralSearchBarViewProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const focusOnInput = () => inputRef.current?.focus();

  return (
    <>
      <div className="general-search-bar--container" ref={ref}>
        <SearchBar
          name="search-app"
          value={searchText}
          placeholder="Search..."
          className="search"
          onChange={(e: any) => setSearchText(e.target.value)}
          onClear={() => clearSearch()}
          onFocus={() => showDropDown()}
          renderIcon={!isLoading ? null : <SwitchAppInlineLoadingIcon />}
          // onKeyDown={() => {}}
          ref={inputRef}
        />
      </div>

      <div className={`dropdown--container ${dropDownClassName || ""}`}>
        <Card
          header={
            isEmptyQuery ? null : (
              <div className="show-all-link--container">
                <Link
                  to={`/search/all-results?search-text=${searchText}`}
                  className="general-search--link show-all--link"
                >
                  <div className="search-link--container">
                    Show all results for {searchText}
                  </div>
                </Link>
              </div>
            )
          }
        >
          {/* Show all results link */}
          {!isEmptyQuery && isEmptyResult ? (
            <div className="empty-results--container flex-container col justify-content--center align-items--center">
              <SearchIcon size={"32"} />
              <p className="font-ctm--bold">No search results found</p>
              <p className="">Please try using a different search query</p>
            </div>
          ) : null}

          {/* Show suggested query types */}
          {isEmptyQuery ? (
            <div className="empty-results--container flex-container col">
              <SectionSubheader
                title="SUGGESTED SEARCH"
                // className="search-section--header"
              />

              <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.emailSearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.emailSearchKey}</span>{" "}
                <span>
                  e.g. {generalSearchConstants.emailSearchKey}
                  useremail@email.com
                </span>
              </div>

              <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.amountSearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.amountSearchKey}</span>{" "}
                <span>
                  e.g. {generalSearchConstants.amountSearchKey}1500.53
                </span>
              </div>

              <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.currencySearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.currencySearchKey}</span>{" "}
                <span>e.g. {generalSearchConstants.currencySearchKey}USD</span>
              </div>

              <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.statusSearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.statusSearchKey}</span>{" "}
                <span>
                  e.g. {generalSearchConstants.statusSearchKey}successful
                </span>
              </div>

              {/* <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.dateSearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.dateSearchKey}</span>{" "}
                <span>
                  e.g. {generalSearchConstants.dateSearchKey}12 June, 2022
                </span>
              </div> */}

              <div
                onClick={() => {
                  setSearchText(`${generalSearchConstants.countrySearchKey}`);
                  focusOnInput();
                }}
                className="search-link--container pattern-search--helper flex-container justify-content--between align-items--center"
              >
                <span>{generalSearchConstants.countrySearchKey}</span>{" "}
                <span>e.g. {generalSearchConstants.countrySearchKey}NG</span>
              </div>
            </div>
          ) : null}

          {!data?.payments || data.payments.length === 0 ? null : (
            <div className="search-section--container">
              {/* List payments here */}
              <SectionSubheader
                title="PAYMENTS"
                className="search-section--header"
              />
              {data?.payments.map((tx) => {
                const statusIndicator = getPaymentStatusIndicator(tx);

                return (
                  <Link
                    to={`/payments/${tx.id}/details`}
                    className="general-search--link"
                    state={{
                      transactionToViewDetails: tx,
                    }}
                  >
                    <div className="search-link--container flex-container align-items--start">
                      <div className="flex-box flex-container align-items--center">
                        <WalletIcon />

                        <CurrencyDisplay
                          amount={tx.amount}
                          currencyOrSymbol={tx.currency}
                          amountFontSize={16}
                          symbolFontSize={12}
                          amountClassName={""}
                          symbolClassName={"ml-5"}
                        />
                        <span className="ml-5 gateway-display">
                          <img
                            alt="gateway"
                            src={`/gateway-logos/${tx.gateway_code}-icon.svg`}
                            className="gateway-icon--tiny"
                          />
                          {tx.gateway_code}
                        </span>
                        <span className="ml-5">{tx.tx_ref}</span>

                        <Badge
                          text={statusIndicator.statusText}
                          statusColor={statusIndicator.statusType}
                          hideIndicator
                          className="ml-5"
                        />
                      </div>

                      <span className="general-search--date">
                        {moment(tx.created_at).format("DD MMM, YYYY")}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!data?.customers || data.customers.length === 0 ? null : (
            <div className="search-section--container">
              {/* List customers here */}
              <SectionSubheader
                title="CUSTOMERS"
                className="search-section--header"
              />
              {data?.customers.map((cust) => (
                <Link
                  to={`/customers/${cust.id}/details`}
                  className="general-search--link"
                >
                  <div className="search-link--container flex-container align-items--center">
                    <UserFilledIcon />
                    <span className="ml-5">{cust.email}</span>
                    <span className="ml-5">{cust.full_name}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
};
